import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Send, Paperclip, Mic, X, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const ModernChatInput = ({ 
  value, 
  onChange, 
  onSend, 
  disabled = false,
  placeholder = "Type your response..."
}) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 300);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value]);

  // Focus on mount and when enabled
  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      
      recognitionInstance.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        onChange(value + transcript);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        toast.error('Speech recognition error. Please try again.');
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, []);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'text/plain', 'text/markdown'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Unsupported file type`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setAttachedFiles([...attachedFiles, ...validFiles]);
      toast.success(`${validFiles.length} file(s) attached`);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setAttachedFiles(attachedFiles.filter((_, i) => i !== index));
    toast.info('File removed');
  };

  const toggleVoiceInput = () => {
    if (!recognition) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    
    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
      toast.info('Stopped listening');
    } else {
      recognition.start();
      setIsRecording(true);
      toast.info('Listening...');
    }
  };

  const handleSend = () => {
    onSend(); // Let sendMessage use the input state
    setAttachedFiles([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="relative">
      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-dark-bg-tertiary rounded-lg text-sm"
            >
              {file.type.startsWith('image/') ? (
                <ImageIcon className="h-4 w-4 text-primary" />
              ) : (
                <FileText className="h-4 w-4 text-primary" />
              )}
              <span className="text-text-dark dark:text-dark-text truncate max-w-[150px]">
                {file.name}
              </span>
              <button
                onClick={() => removeFile(index)}
                className="text-text-gray hover:text-destructive transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Input Container */}
      <div className="glass border border-gray-300 dark:border-[#2e2e2d] rounded-2xl shadow-md hover:shadow-lg transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <div className="flex items-end gap-3 p-4">
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Attachment Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10"
            title="Attach files (PDF, images, text)"
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 resize-none bg-transparent border-0 focus:outline-none focus:ring-0 text-text-dark dark:text-dark-text placeholder:text-text-gray placeholder:dark:text-dark-text-secondary text-base py-2 px-2 min-h-[60px] max-h-[300px] overflow-y-auto"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
            }}
          />

          {/* Voice Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleVoiceInput}
            disabled={disabled}
            className={`h-9 w-9 p-0 rounded-xl transition-all ${
              isRecording 
                ? 'bg-red-500 hover:bg-red-600 text-white' 
                : 'hover:bg-primary/10'
            }`}
            title={isRecording ? 'Stop recording' : 'Voice input'}
          >
            <Mic className={`h-4 w-4 ${isRecording ? 'animate-pulse' : ''}`} />
          </Button>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={disabled || !value.trim()}
            className="h-9 w-9 p-0 rounded-xl gradient-primary text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Helper Text */}
      <div className="flex items-center justify-between px-2 mt-2">
        <p className="text-xs text-text-gray dark:text-dark-text-secondary">
          Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-gray-100 dark:bg-dark-bg-tertiary rounded">Enter</kbd> to send • <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-gray-100 dark:bg-dark-bg-tertiary rounded">Shift + Enter</kbd> for new line
        </p>
      </div>
    </div>
  );
};

export default ModernChatInput;
